/**
 * Host-owned gallery viewer.
 *
 * A template says *where* the gallery grid sits and what a tile looks like; the
 * viewer itself is Jubiel's, so focus handling, the Escape key and the close
 * affordance behave the same on every design.
 */

import { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

export type PublicLightboxProps = {
  images: string[];
  /** Index of the open image, or null when the viewer is closed. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
  /** Optional theming, so the viewer sits inside the template's palette. */
  scrim?: string;
  surface?: string;
  textColor?: string;
};

export function PublicLightbox({
  images,
  index,
  onIndexChange,
  scrim = "rgba(17,24,39,0.95)",
  surface = "#ffffff",
  textColor = "#111827",
}: PublicLightboxProps) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null && images.length > 0;

  const step = useCallback(
    (delta: number) => {
      if (index === null) return;
      onIndexChange((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onIndexChange(null);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };

    document.addEventListener("keydown", onKeyDown);
    // The page behind must not scroll while the viewer owns the screen.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onIndexChange, step]);

  if (!open) return null;

  const buttonStyle = {
    background: surface,
    color: textColor,
    boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 backdrop-blur-xl"
      style={{ background: scrim }}
      role="dialog"
      aria-modal="true"
      aria-label={t("events.form.template.gallery")}
      onClick={() => onIndexChange(null)}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={() => onIndexChange(null)}
        className="absolute top-4 right-4 sm:top-8 sm:right-8 rounded-full p-3 sm:p-4 transition-opacity hover:opacity-80"
        style={buttonStyle}
        aria-label={t("common.close")}
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); step(-1); }}
            className="absolute left-4 rounded-full p-3 transition-opacity hover:opacity-80"
            style={buttonStyle}
            aria-label={t("common.previous")}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); step(1); }}
            className="absolute right-4 rounded-full p-3 transition-opacity hover:opacity-80"
            style={buttonStyle}
            aria-label={t("common.next")}
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </>
      )}

      <div className="max-h-[90vh] max-w-5xl" onClick={(event) => event.stopPropagation()}>
        <img
          src={images[index]}
          alt={`${t("events.form.template.gallery")} ${index + 1}`}
          className="max-h-[80vh] w-full rounded-sm object-contain"
          decoding="async"
        />
      </div>
    </div>
  );
}
